import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { api } from '../../services/api';
import FinanceSummaryCards from './FinanceSummaryCards';
import FinanceHistoryCharts from './FinanceHistoryCharts';
import MonthSalesDetailDialog from './MonthSalesDetailDialog';

export interface DashboardData {
  summary: {
    collected_week: number;
    collected_week_count: number;
    collected_month: number;
    collected_month_count: number;
    pending_total: number;
    sales_month_total: number;
    sales_month_count: number;
    sales_change_pct: number;
  };
  weekly_history: Array<{
    week_start: string;
    week_end: string;
    amount: number;
    count: number;
    label: string;
  }>;
  monthly_history: Array<{
    month: string;
    label: string;
    amount: number;
    change_pct?: number;
  }>;
  weekly_sales_history: Array<{
    week_start: string;
    week_end: string;
    amount: number;
    count: number;
    label: string;
    credit_amount: number;
    product_amount: number;
    credit_count: number;
    product_count: number;
    product_cost: number;
  }>;
  monthly_sales_history: Array<{
    month: string;
    label: string;
    amount: number;
    change_pct?: number;
    credit_amount: number;
    product_amount: number;
    credit_count: number;
    product_count: number;
    product_cost: number;
  }>;
}

type FilterType = 'all' | 'credits' | 'products';

export default function FinancePage() {
  const [filter, setFilter] = useState<FilterType>('all');
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [monthDetail, setMonthDetail] = useState<{ month: string; label: string } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/finance/dashboard-v2', { params: { filter } });
      setData(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newVal: FilterType | null) => {
    if (newVal) setFilter(newVal);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          Finanzas
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={handleFilterChange}
            size="small"
          >
            <ToggleButton value="all">Todo</ToggleButton>
            <ToggleButton value="credits">Créditos</ToggleButton>
            <ToggleButton value="products">Productos</ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={fetchData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FinanceSummaryCards summary={data.summary} />
          <FinanceHistoryCharts
            weeklyHistory={data.weekly_history}
            monthlyHistory={data.monthly_history}
            weeklySalesHistory={data.weekly_sales_history}
            monthlySalesHistory={data.monthly_sales_history}
            onMonthClick={(month, label) => setMonthDetail({ month, label })}
          />
        </Box>
      ) : null}

      <MonthSalesDetailDialog
        open={!!monthDetail}
        month={monthDetail?.month ?? null}
        label={monthDetail?.label ?? ''}
        filter={filter}
        onClose={() => setMonthDetail(null)}
      />
    </Box>
  );
}
